<template>
  <!-- classic sunset notice: the one-time "welcome to horizon" sheet for
       accounts the migration moved off classic. mounted once in App.vue,
       outside the theme branches, and self-gated on
       preferences.classic_sunset_notice === 'pending' (server-side state;
       see utils/classic_sunset_notice.js for why not localStorage). header and footer are ours so the sheet reads
       as a welcome rather than a bootstrap dialog. every way of closing it
       counts as seen: the point is that people know where the picker went,
       not that they press a particular button. -->
  <b-modal
    id="fi-classic-sunset-notice"
    centered
    hide-header
    hide-footer
    size="md"
    content-class="fi-csn"
    aria-label="Welcome to Horizon"
    @hide="ack"
    @hidden="onHidden"
  >
    <div class="fi-csn-inner">
      <div class="fi-csn-eyebrow">A new look</div>
      <h2 class="fi-csn-title">Welcome to Horizon</h2>
      <p class="fi-csn-lead">
        Fleet Integrations now opens in the Horizon theme. It is built for phones and tablets as well as the desktop, and new features arrive here first.
      </p>
      <p class="fi-csn-body">
        The Classic theme is being retired. Until then, your initials in the top right corner hold the Theme picker, where you can also try Midnight, the dark side of Horizon.
      </p>
      <!-- the real theme picker, live: the same control as the avatar menu
           (markup plus the global .profile-theme-* rules from navbar.vue), so
           people can switch right here. every pick applies on the spot and
           the sheet stays open, classic included: App.vue mounts this sheet
           outside the theme branches, so the shell swaps underneath it.
           swatch colors are the real ones: structure / action / admin per
           theme. -->
      <div class="fi-csn-picker">
        <div class="profile-theme-row">
          <span class="profile-theme-label">Theme</span>
          <div class="profile-theme-toggle" role="group" aria-label="Theme">
            <button
              type="button"
              class="profile-theme-opt"
              :class="{ 'profile-theme-opt--active': themeChoice === 'original' }"
              :aria-pressed="String(themeChoice === 'original')"
              @click="pickTheme('original')"
            >
              <span class="profile-theme-swatches" aria-hidden="true">
                <span class="profile-theme-swatch" style="background:#4682b4"></span>
                <span class="profile-theme-swatch" style="background:#007bff"></span>
                <span class="profile-theme-swatch" style="background:#b58100"></span>
              </span>
              <span>Classic</span>
            </button>
            <button
              type="button"
              class="profile-theme-opt"
              :class="{ 'profile-theme-opt--active': themeChoice === 'new' }"
              :aria-pressed="String(themeChoice === 'new')"
              @click="pickTheme('new')"
            >
              <span class="profile-theme-swatches" aria-hidden="true">
                <span class="profile-theme-swatch" style="background:#15324d"></span>
                <span class="profile-theme-swatch" style="background:#007ac7"></span>
                <span class="profile-theme-swatch" style="background:#7b3fb0"></span>
              </span>
              <span>Horizon</span>
            </button>
            <button
              type="button"
              class="profile-theme-opt"
              :class="{ 'profile-theme-opt--active': themeChoice === 'midnight' }"
              :aria-pressed="String(themeChoice === 'midnight')"
              @click="pickTheme('midnight')"
            >
              <span class="profile-theme-swatches" aria-hidden="true">
                <span class="profile-theme-swatch" style="background:#0e1b2b"></span>
                <span class="profile-theme-swatch" style="background:#6fb1e8"></span>
                <span class="profile-theme-swatch" style="background:#7b3fb0"></span>
              </span>
              <span>Midnight</span>
            </button>
          </div>
        </div>
      </div>
      <div class="fi-csn-actions">
        <!-- the spotlight is answered by the horizon top bar only; on classic
             the picker they just used is the one in the sheet anyway -->
        <b-button v-if="themeChoice !== 'original'" variant="outline-secondary" @click="showMeWhere">Show me where</b-button>
        <b-button variant="primary" class="fi-csn-primary" @click="done">Done</b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapState } from 'vuex'
import { dismiss_classic_sunset_notice, update_site_theme } from '@/networking/apis'
import { shouldShowClassicSunsetNotice, withNoticeDismissed } from '@/utils/classic_sunset_notice'

const MODAL_ID = 'fi-classic-sunset-notice'

// set once the sheet has been dismissed on this page load. the vuex user is
// deliberately left carrying 'pending' (see ack), so a remount of the horizon
// branch in the same session (a theme flip to classic and back) has to be
// told another way that the notice was already seen.
let dismissedThisPage = false

export default {
  name: 'FiClassicSunsetNotice',
  data() {
    return {
      // once per mount: the gate in the store stays true for the rest of the
      // page load (ack leaves the vuex user alone), so this and the module
      // flag above are what keep the sheet from rising twice
      shown: false,
      // @hide fires for every way of closing (buttons, esc, backdrop); the
      // dismissal is recorded once
      acked: false,
      // "Show me where" was pressed: the spotlight fires from @hidden, once
      // the sheet is gone, so the top bar's document-click close cannot race
      // the popover open
      showMe: false,
      // a theme was picked in the sheet: the api's site-theme write already
      // flips the notice to dismissed, so ack() skips its own round trip
      themePicked: false,
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      siteTheme: state => state.profile.site_theme,
    }),
    shouldShow() {
      return shouldShowClassicSunsetNotice(this.user, this.siteTheme)
    },
    // picker state: unknown stored values render as classic, matching the
    // applyFiTheme coercion in the profile store (same as the avatar menu)
    themeChoice() {
      return (this.siteTheme === 'new' || this.siteTheme === 'midnight') ? this.siteTheme : 'original'
    },
  },
  watch: {
    // the user and theme can land after mount (login commits them in turn)
    shouldShow(v) {
      if (v) this.maybeShow()
    },
  },
  mounted() {
    this._showTimer = null
    this.maybeShow()
  },
  beforeDestroy() {
    if (this._showTimer) clearTimeout(this._showTimer)
  },
  methods: {
    maybeShow() {
      if (this.shown || dismissedThisPage || !this.shouldShow) return
      this.shown = true
      // let the page paint first so the sheet rises over a finished screen
      this._showTimer = setTimeout(() => {
        this._showTimer = null
        this.$bvModal.show(MODAL_ID)
      }, 600)
    },
    // closes the sheet; whatever was picked is already applied and saved
    done() {
      this.$bvModal.hide(MODAL_ID)
    },
    showMeWhere() {
      this.showMe = true
      this.$bvModal.hide(MODAL_ID)
    },
    onHidden() {
      if (!this.showMe) return
      this.showMe = false
      // fi_top_nav opens the account popover and rings the theme picker
      this.$root.$emit('fi-spotlight-theme-picker')
    },
    // the live picker. same optimistic apply and rollback as the avatar menu
    // (fi_top_nav.vue setSiteTheme). picking a theme here counts as having
    // seen the notice: the api's site-theme write flips it to dismissed.
    pickTheme(theme) {
      const next = (theme === 'new' || theme === 'midnight') ? theme : 'original'
      if (next === this.siteTheme) return
      this.themePicked = true
      this.markSeenLocally()
      this.applyTheme(next)
    },
    async applyTheme(next) {
      const prev = this.siteTheme
      this.$store.commit('profile/set_site_theme', next)
      try {
        await update_site_theme(next)
      } catch (e) {
        this.$store.commit('profile/set_site_theme', prev)
      }
    },
    // record locally that the sheet has been seen, without a round trip:
    // the localStorage copy of the user is what App.vue re-hydrates the
    // store from on reload, and the module flag stops a remount in this
    // page load from raising the sheet again. the vuex user itself is left
    // alone on purpose: replacing profile.user (the user_detail.vue idiom)
    // fires the deep `user` watchers on ~50 pages, written for the tenant
    // switch, and every one of them would refetch whatever sits behind this
    // sheet. nothing but this notice reads the key, and the next login
    // brings the server copy in anyway.
    markSeenLocally() {
      dismissedThisPage = true
      try {
        localStorage.setItem('user', JSON.stringify(withNoticeDismissed(this.user)))
      } catch (_) { /* storage full or blocked; the server copy carries it */ }
    },
    async ack() {
      if (this.acked) return
      this.acked = true
      // written before the await so a slow save cannot re-plant the key
      // after a logout has cleared storage
      this.markSeenLocally()
      // a theme picked in the sheet is already on record: the site-theme
      // write flips the notice to dismissed on the server itself
      if (this.themePicked) return
      try {
        await dismiss_classic_sunset_notice()
      } catch (e) {
        // a lost write only means the notice shows again next login
        // eslint-disable-next-line no-console
        console.warn('classic sunset notice: could not record the dismissal', e)
      }
    },
  },
}
</script>

<style scoped>
/* tokens only: the surface / text / line tokens re-resolve dark under
   midnight and midnight.css already darkens .modal-content, so nothing here
   hardcodes a light background or dark ink. */
.fi-csn-inner {
  padding: 8px 6px 4px;
}
.fi-csn-eyebrow {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--fi-brand);
  margin-bottom: 6px;
}
.fi-csn-title {
  font-size: 22px;
  font-weight: 600;
  line-height: 1.25;
  color: var(--fi-text);
  margin: 0 0 10px;
}
.fi-csn-lead {
  font-size: 15px;
  line-height: 1.5;
  color: var(--fi-text);
  margin: 0 0 10px;
}
.fi-csn-body {
  font-size: 14px;
  line-height: 1.5;
  color: var(--fi-text-muted);
  margin: 0 0 14px;
}
/* the live picker: a small card at about the popover's width. the row's own
   padding, the segment hover and the active fill all come from navbar.vue's
   global .profile-theme-* rules, so it behaves exactly like the menu. */
.fi-csn-picker {
  max-width: 320px;
  background: var(--fi-surface-sunken);
  border: 1px solid var(--fi-line);
  border-radius: var(--fi-radius-md);
  padding: 2px 0;
}
/* --fi-brand is not re-resolved for midnight and lands at 3.4:1 on the dark
   modal surface; the lifted chrome accent is the readable brand on dark, the
   same call fi_top_nav.vue makes for the spotlit picker label */
:root[data-fi-theme="midnight"] .fi-csn-eyebrow { color: var(--fi-chrome-accent); }
/* the picker's midnight skin lives in fi_top_nav.vue, which cannot reach
   this sheet (it is portaled to body), so the same values are restated here */
:root[data-fi-theme="midnight"] .fi-csn-picker .profile-theme-label { color: var(--fi-chrome-muted); }
:root[data-fi-theme="midnight"] .fi-csn-picker .profile-theme-toggle {
  border-color: rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.06);
}
:root[data-fi-theme="midnight"] .fi-csn-picker .profile-theme-opt { color: var(--fi-chrome-muted); }
:root[data-fi-theme="midnight"] .fi-csn-picker .profile-theme-opt:hover { color: var(--fi-chrome-ink-strong); }
:root[data-fi-theme="midnight"] .fi-csn-picker .profile-theme-opt--active,
:root[data-fi-theme="midnight"] .fi-csn-picker .profile-theme-opt--active:hover { color: #fff; }
:root[data-fi-theme="midnight"] .fi-csn-picker .profile-theme-swatch {
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.25);
}
:root[data-fi-theme="midnight"] .fi-csn-picker .profile-theme-opt--active .profile-theme-swatch {
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.65);
}
.fi-csn-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 18px;
}
/* keeps the primary on the right when it is the only button (classic) */
.fi-csn-primary {
  margin-left: auto;
}
</style>
